(() => {
  if (window.KryoshiftPredictiveSearchLoaded) return;
  window.KryoshiftPredictiveSearchLoaded = true;

  const SELECTORS = {
    root: '[data-predictive-search-modal]',
    open: '[data-search-modal-open]',
    close: '[data-search-modal-close]',
    dialog: '[data-search-modal-dialog]',
    form: '[data-search-form]',
    input: '[data-search-input]',
    initial: '[data-search-initial]',
    results: '[data-search-results]',
    status: '[data-search-status]',
    products: '[data-products-results]',
    collections: '[data-collections-results]',
    pages: '[data-pages-results]',
    collectionsGroup: '[data-collections-group]',
    pagesGroup: '[data-pages-group]',
    empty: '[data-search-empty]',
    viewAll: '[data-search-view-all]'
  };

  const escapeHtml = (value = '') =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  class PredictiveSearchModal {
    constructor(root) {
      if (!root || root.dataset.initialised === 'true') return;

      root.dataset.initialised = 'true';

      this.root = root;
      this.dialog = root.querySelector(SELECTORS.dialog);
      this.form = root.querySelector(SELECTORS.form);
      this.input = root.querySelector(SELECTORS.input);
      this.initial = root.querySelector(SELECTORS.initial);
      this.results = root.querySelector(SELECTORS.results);
      this.status = root.querySelector(SELECTORS.status);
      this.productsContainer = root.querySelector(SELECTORS.products);
      this.collectionsContainer = root.querySelector(SELECTORS.collections);
      this.pagesContainer = root.querySelector(SELECTORS.pages);
      this.collectionsGroup = root.querySelector(SELECTORS.collectionsGroup);
      this.pagesGroup = root.querySelector(SELECTORS.pagesGroup);
      this.empty = root.querySelector(SELECTORS.empty);
      this.viewAll = root.querySelector(SELECTORS.viewAll);
      this.closeButton = root.querySelector(SELECTORS.close);

      this.predictiveUrl = root.dataset.predictiveUrl;
      this.searchUrl = root.dataset.searchUrl;
      this.productLimit = Number(root.dataset.productLimit || 10);
      this.minimumCharacters = Number(root.dataset.minimumCharacters || 2);
      this.currency = root.dataset.currency || 'GBP';
      this.locale = root.dataset.locale || 'en-GB';
      this.badgeTag = (root.dataset.badgeTag || '').trim().toLowerCase();
      this.badgeLabel = root.dataset.badgeLabel || '';
      this.priceSuffix = root.dataset.priceSuffix || '';

      this.cache = new Map();
      this.abortController = null;
      this.debounceTimer = null;
      this.lastFocusedElement = null;
      this.currentQuery = '';
      this.closeTimer = null;

      this.bindEvents();
    }

    bindEvents() {
      this.openButtons = [
        ...document.querySelectorAll(
          `${SELECTORS.open}[aria-controls="${this.root.id}"]`
        )
      ];

      this.openButtons.forEach((button) => {
        button.addEventListener('click', () => this.open(button));
      });

      this.closeButton?.addEventListener('click', () => this.close());

      this.root.addEventListener('mousedown', (event) => {
        if (event.target === this.root) {
          this.close();
        }
      });

      this.root.addEventListener('keydown', (event) =>
        this.handleKeydown(event)
      );

      this.input?.addEventListener('input', () => this.handleInput());

      this.form?.addEventListener('submit', (event) =>
        this.handleSubmit(event)
      );
    }

    open(trigger) {
      window.clearTimeout(this.closeTimer);

      this.lastFocusedElement = trigger || document.activeElement;
      this.root.hidden = false;
      this.input?.setAttribute('aria-expanded', 'false');

      document.body.classList.add('ks-search-modal-open');

      requestAnimationFrame(() => {
        this.root.classList.add('is-open');

        window.setTimeout(() => {
          this.input?.focus();
        }, 60);
      });
    }

    close() {
      if (this.root.hidden) return;

      this.abortController?.abort();
      clearTimeout(this.debounceTimer);

      this.root.classList.remove('is-open', 'is-loading');
      document.body.classList.remove('ks-search-modal-open');
      this.input?.setAttribute('aria-expanded', 'false');

      this.closeTimer = window.setTimeout(() => {
        this.root.hidden = true;
        this.lastFocusedElement?.focus?.();
      }, 180);
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = [
        ...this.root.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ].filter((element) => {
        return !element.hidden && element.offsetParent !== null;
      });

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleSubmit(event) {
      const query = this.input?.value.trim();

      if (!query) {
        event.preventDefault();
        this.input?.focus();
      }
    }

    handleInput() {
      const query = this.input.value.trim();

      this.currentQuery = query;

      clearTimeout(this.debounceTimer);

      if (query.length < this.minimumCharacters) {
        this.abortController?.abort();
        this.setLoading(false);
        this.showInitial();
        return;
      }

      this.debounceTimer = window.setTimeout(() => {
        this.getResults(query);
      }, 220);
    }

    async getResults(query) {
      const cacheKey = query.toLowerCase();

      if (this.cache.has(cacheKey)) {
        this.renderResults(this.cache.get(cacheKey), query);
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();

      this.setLoading(true);

      try {
        const url = new URL(
          this.predictiveUrl,
          window.location.origin
        );

        url.searchParams.set('q', query);
        url.searchParams.set(
          'resources[type]',
          'product,collection,page'
        );
        url.searchParams.set(
          'resources[limit]',
          String(this.productLimit)
        );
        url.searchParams.set(
          'resources[limit_scope]',
          'each'
        );
        url.searchParams.set(
          'resources[options][unavailable_products]',
          'last'
        );
        url.searchParams.set(
          'resources[options][fields]',
          'title,product_type,variants.title,vendor,tag,body'
        );

        const response = await fetch(url.toString(), {
          headers: {
            Accept: 'application/json'
          },
          signal: this.abortController.signal
        });

        if (!response.ok) {
          throw new Error(
            `Predictive search failed: ${response.status}`
          );
        }

        const data = await response.json();
        const resultSet = data?.resources?.results || {};

        this.cache.set(cacheKey, resultSet);

        if (this.currentQuery === query) {
          this.renderResults(resultSet, query);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
          this.renderResults({}, query);
        }
      } finally {
        if (this.currentQuery === query) {
          this.setLoading(false);
        }
      }
    }

    renderResults(resultSet, query) {
      const products = Array.isArray(resultSet.products)
		  ? resultSet.products
		      .filter((product) => {
		        const rawPrice = product.price_min ?? product.price;
		        const numericPrice = Number.parseFloat(rawPrice);

		        const tags = Array.isArray(product.tags)
		          ? product.tags.map((tag) => String(tag).trim().toLowerCase())
		          : String(product.tags || '')
		              .split(',')
		              .map((tag) => tag.trim().toLowerCase());

		        const isHiddenFromSearch = tags.includes('hide_from_search');

		        return (
		          Number.isFinite(numericPrice) &&
		          numericPrice > 0 &&
		          !isHiddenFromSearch
		        );
		      })
		      .slice(0, this.productLimit)
		  : [];

      const collections = Array.isArray(resultSet.collections)
        ? resultSet.collections.slice(0, 3)
        : [];

      const pages = Array.isArray(resultSet.pages)
        ? resultSet.pages.slice(0, 3)
        : [];

      const total =
        products.length +
        collections.length +
        pages.length;

      this.productsContainer.innerHTML = products
        .map((product) => this.productCard(product))
        .join('');

      this.collectionsContainer.innerHTML = collections
        .map((item) => this.suggestionLink(item))
        .join('');

      this.pagesContainer.innerHTML = pages
        .map((item) => this.suggestionLink(item))
        .join('');

      this.collectionsGroup.hidden =
        collections.length === 0;

      this.pagesGroup.hidden =
        pages.length === 0;

      this.empty.hidden = total !== 0;

      this.viewAll.href =
        `${this.searchUrl}?q=${encodeURIComponent(query)}&type=product`;

      this.initial.hidden = true;
      this.results.hidden = false;
      this.results.setAttribute('aria-busy', 'false');
      this.input.setAttribute('aria-expanded', 'true');

      this.status.textContent =
        total === 1
          ? '1 search result found.'
          : `${total} search results found.`;
    }

    productCard(product) {
      const title = escapeHtml(
        product.title || 'Product'
      );

      const url = escapeHtml(
        product.url || '#'
      );

      const image =
        product.featured_image?.url ||
        product.image ||
        '';

      const imageAlt = escapeHtml(
        product.featured_image?.alt ||
        product.title ||
        ''
      );

      const tags = Array.isArray(product.tags)
        ? product.tags.map((tag) =>
            String(tag).toLowerCase()
          )
        : String(product.tags || '')
            .split(',')
            .map((tag) =>
              tag.trim().toLowerCase()
            );

      const showBadge =
        this.badgeLabel &&
        this.badgeTag &&
        tags.includes(this.badgeTag);

      const rawPrice =
        product.price_min ??
        product.price ??
        0;

      const price =
        this.formatMoney(rawPrice);

      return `
        <a class="ks-search-product" href="${url}">
          ${
            showBadge
              ? `<span class="ks-search-product__badge">${escapeHtml(
                  this.badgeLabel
                )}</span>`
              : ''
          }

          <span class="ks-search-product__image${
            image
              ? ''
              : ' ks-search-product__image--empty'
          }">
            ${
              image
                ? `
                  <img
                    src="${escapeHtml(image)}"
                    alt="${imageAlt}"
                    loading="lazy"
                    width="280"
                    height="190"
                  >
                `
                : '<span>No image</span>'
            }
          </span>

          <span class="ks-search-product__title">
            ${title}
          </span>

          <span class="ks-search-product__price">
            ${escapeHtml(price)}
            ${
              this.priceSuffix
                ? `<small>${escapeHtml(
                    this.priceSuffix
                  )}</small>`
                : ''
            }
          </span>
        </a>
      `;
    }

    suggestionLink(item) {
      return `
        <li>
          <a href="${escapeHtml(item.url || '#')}">
            ${escapeHtml(item.title || '')}
          </a>
        </li>
      `;
    }

    formatMoney(value) {
      const number = Number.parseFloat(value);

      if (!Number.isFinite(number)) {
        return '';
      }

      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(number);
      } catch (error) {
        return `${this.currency} ${number.toFixed(2)}`;
      }
    }

    showInitial() {
      this.initial.hidden = false;
      this.results.hidden = true;
      this.results.setAttribute('aria-busy', 'false');
      this.input.setAttribute('aria-expanded', 'false');
      this.status.textContent = '';
    }

    setLoading(isLoading) {
      this.root.classList.toggle(
        'is-loading',
        isLoading
      );

      this.results?.setAttribute(
        'aria-busy',
        String(isLoading)
      );
    }
  }

  const initialise = (scope = document) => {
    scope
      .querySelectorAll(SELECTORS.root)
      .forEach((root) => {
        new PredictiveSearchModal(root);
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialise()
    );
  } else {
    initialise();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => initialise(event.target)
  );
})();